import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Menu } from 'lucide-react';
import { useAppUIContext } from '../../context/AppUIContext';
import { cn } from '../../lib/utils';
import { MyNotifs } from '../../services/MyNotifs';

interface MenuToggleButtonProps {
    className?: string;
}

export const MenuToggleButton: React.FC<MenuToggleButtonProps> = ({ className }) => {
    const { setIsMobileMenuOpen } = useAppUIContext();
    const [unreadCount, setUnreadCount] = useState(MyNotifs.NrOfNew);

    const isLongPressRef = useRef(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    // MyNotifs 변경 구독
    useEffect(() => {
        const unsubscribe = MyNotifs.subscribeToNotificationUpdates(() => {
            setUnreadCount(MyNotifs.NrOfNew);
        });
        return unsubscribe;
    }, []);

    const startPress = useCallback(() => {
        isLongPressRef.current = false;
        timerRef.current = setTimeout(() => {
            isLongPressRef.current = true;
            window.dispatchEvent(new Event('open-workspace-sitemap'));
            if (navigator.vibrate) navigator.vibrate(50);
        }, 500); // 500ms long press
    }, []);

    const cancelPress = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const handleClick = useCallback((e: React.MouseEvent) => {
        if (isLongPressRef.current) {
            e.preventDefault();
            e.stopPropagation();
            isLongPressRef.current = false;
            return;
        }
        setIsMobileMenuOpen(true);
    }, [setIsMobileMenuOpen]);

    return (
        <Button
            variant="ghost"
            size="icon"
            onClick={handleClick}
            onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onTouchStart={startPress}
            onTouchEnd={cancelPress}
            onMouseDown={startPress}
            onMouseUp={cancelPress}
            onMouseLeave={cancelPress}
            style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
            className={cn("lg:hidden relative select-none", className)}
        >
            <Menu className="h-5 w-5 pointer-events-none" />
            {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-destructive text-on-primary-text text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 pointer-events-none">
                    {unreadCount > 99 ? '99+' : unreadCount}
                </span>
            )}
            <span className="sr-only">Toggle Menu</span>
        </Button>
    );
};
